import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { Reveal } from "../components/Reveal";
import Seo from "../components/Seo";
import { SERVICES, PILLARS, SERVE } from "../data/content";

export default function Solutions() {
  return (
    <div data-testid="solutions-page" className="pt-32 md:pt-44">
      <Seo
        title="What We Do — Operations, Marketing & Deals | Quexify"
        description="One AI-augmented right hand runs your operations, marketing, leads, calendar and closings. Real estate expertise, human-checked, priced on outcomes. Close more, chase less."
        path="/solutions"
      />

      {/* HERO */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-10">
        <Reveal>
          <h1 className="font-display text-5xl md:text-8xl tracking-tighter font-medium leading-[0.9] max-w-4xl">
            One right hand.<br />Everything but selling.
          </h1>
          <p className="mt-8 text-lg md:text-xl text-neutral-500 max-w-2xl leading-relaxed">
            Selling is yours. Everything that surrounds it, transactions, marketing, leads, calendar and admin, belongs to a dedicated Quexify operator, built with real estate expertise and backed by AI. One partner, your whole back office, under one roof.
          </p>
        </Reveal>
      </section>

      {/* CAPABILITIES */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-10 py-24 md:py-36">
        <Reveal className="max-w-2xl mb-16 md:mb-20">
          <h2 className="font-display text-4xl md:text-6xl tracking-tighter font-medium leading-[0.98]">
            What your right hand runs.
          </h2>
        </Reveal>
        <div className="grid md:grid-cols-2 gap-x-16 gap-y-20 md:gap-y-28">
          {SERVICES.map((s, i) => {
            const Icon = s.icon;
            return (
              <Reveal key={s.title} delay={(i % 2) * 0.08}>
                <div className="group" data-testid={`solution-${i}`}>
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-full border border-black/10 flex items-center justify-center transition-colors duration-500 group-hover:bg-[#0a0a0a] group-hover:text-white">
                      <Icon size={20} strokeWidth={1.5} />
                    </div>
                    <span className="font-display text-6xl tracking-tighter text-neutral-200">0{i + 1}</span>
                  </div>
                  <h3 className="mt-8 font-display text-3xl md:text-4xl tracking-tight font-medium">{s.title}</h3>
                  <p className="mt-4 text-lg text-neutral-500 leading-relaxed max-w-xl">{s.desc}</p>
                </div>
              </Reveal>
            );
          })}
        </div>
      </section>

      {/* HOW WE'RE DIFFERENT — PILLARS */}
      <section className="bg-[#0a0a0a] text-white">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-24 md:py-36">
          <div className="grid md:grid-cols-12 gap-x-10 gap-y-12">
            <div className="md:col-span-4">
              <Reveal>
                <h2 className="font-display text-4xl md:text-6xl tracking-tighter font-medium leading-[0.98]">
                  Not a VA.<br />A right hand.
                </h2>
                <p className="mt-6 text-neutral-400 leading-relaxed max-w-sm">
                  A virtual assistant waits for instructions. Your right hand owns the outcome, and the way we work is what makes the difference.
                </p>
              </Reveal>
            </div>
            <div className="md:col-span-8 border-t border-white/10">
              {PILLARS.map((p, i) => (
                <Reveal key={p.k} delay={i * 0.05}>
                  <div className="grid sm:grid-cols-12 gap-2 sm:gap-8 items-baseline py-8 md:py-9 border-b border-white/10" data-testid={`solution-pillar-${i}`}>
                    <div className="sm:col-span-4 flex items-baseline gap-3">
                      <span className="font-display text-sm text-neutral-500 tabular-nums">{p.k}</span>
                      <h3 className="font-display text-xl md:text-2xl tracking-tight font-medium">{p.title}</h3>
                    </div>
                    <p className="sm:col-span-8 text-neutral-400 leading-relaxed">{p.desc}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* WHO WE SERVE */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-10 py-24 md:py-36" data-testid="solutions-serve">
        <Reveal className="max-w-2xl mb-14 md:mb-20">
          <h2 className="font-display text-4xl md:text-6xl tracking-tighter font-medium leading-[0.98]">
            Built for the people who move real estate.
          </h2>
        </Reveal>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-black/10 border border-black/10 rounded-3xl overflow-hidden">
          {SERVE.map((s, i) => (
            <Reveal key={s.title} delay={(i % 3) * 0.06}>
              <div className="group bg-white h-full p-8 md:p-10 transition-colors duration-500 hover:bg-[#0a0a0a] hover:text-white" data-testid={`solutions-serve-${i}`}>
                <h3 className="font-display text-2xl tracking-tight font-medium">{s.title}</h3>
                <p className="mt-3 text-neutral-500 group-hover:text-neutral-300 leading-relaxed transition-colors">{s.line}</p>
              </div>
            </Reveal>
          ))}
          <Reveal delay={0.12}>
            <Link to="/book-demo" className="group bg-white h-full p-8 md:p-10 flex flex-col justify-between transition-colors duration-500 hover:bg-[#0a0a0a] hover:text-white" data-testid="solutions-serve-cta">
              <h3 className="font-display text-2xl tracking-tight font-medium">Not sure which fits?</h3>
              <span className="mt-6 inline-flex items-center gap-2 text-neutral-500 group-hover:text-white transition-colors">
                Book a discovery call <ArrowUpRight size={18} />
              </span>
            </Link>
          </Reveal>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-10 pb-24 md:pb-40">
        <Reveal>
          <div className="rounded-3xl bg-[#0a0a0a] text-white px-8 md:px-20 py-20 md:py-28 flex flex-col md:flex-row md:items-end md:justify-between gap-10">
            <h2 className="font-display text-4xl md:text-6xl tracking-tighter font-medium leading-[0.95] max-w-xl">
              Build your operations layer.
            </h2>
            <Link to="/book-demo" className="inline-flex items-center gap-2 rounded-full bg-white text-black px-8 py-4 text-lg transition-transform duration-300 hover:-translate-y-0.5 shrink-0" data-testid="solutions-cta">
              Book a discovery call <ArrowUpRight size={20} />
            </Link>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
